/**
 * Shared referral OTP helpers — checkstatus/otp vs sendOtp, verify,
 * localStorage otpData, hash shapes, digit/paste utilities.
 */

export const OTP_LENGTH = 6;
export const OTP_TTL_SECONDS = 30;
export const OTP_STORAGE_KEY = "otpData";

export type CheckStatusOtpFor = "Referral" | "CheckingStatus";

export function createEmptyOtp(): string[] {
  return Array(OTP_LENGTH).fill("");
}

export function storeOtpData(otpPayload: unknown): void {
  localStorage.setItem(
    OTP_STORAGE_KEY,
    JSON.stringify({ otp: otpPayload, timestamp: Date.now() }),
  );
}

export function getStoredOtpData(): { otp?: any; timestamp?: number } {
  try {
    return JSON.parse(localStorage.getItem(OTP_STORAGE_KEY) || "{}");
  } catch {
    return {};
  }
}

/** True when stored OTP timestamp is older than TTL. Missing timestamp → not expired. */
export function isStoredOtpExpired(): boolean {
  const stored = getStoredOtpData();
  if (!stored?.timestamp) return false;
  const elapsed = Math.floor((Date.now() - stored.timestamp) / 1000);
  return elapsed >= OTP_TTL_SECONDS;
}

/** Hash after checkstatus/otp (Referral / CheckStatus student). */
export function getCheckStatusOtpHash(
  stored: { otp?: any } = getStoredOtpData(),
): unknown {
  return stored?.otp?.response?.hash;
}

/** Hash after /api/referral/sendOtp (corporate). */
export function getCorporateOtpHash(
  stored: { otp?: any } = getStoredOtpData(),
): unknown {
  return stored?.otp;
}

export function formatOtpTimer(timer: number): string {
  const mins = Math.floor(timer / 60);
  const secs = timer % 60;
  return `${mins}:${secs < 10 ? `0${secs}` : secs}`;
}

export function focusOtpInput(idPrefix: string, index: number): void {
  const el = document.getElementById(`${idPrefix}${index}`);
  if (el) (el as HTMLInputElement).focus();
}

export function applyOtpDigit(
  otp: string[],
  index: number,
  value: string,
): { otp: string[]; nextFocusIndex: number | null } | null {
  if (!/\d/.test(value) && value !== "") return null;
  const next = [...otp];
  next[index] = value;
  const nextFocusIndex =
    value && index < otp.length - 1 ? index + 1 : null;
  return { otp: next, nextFocusIndex };
}

export function applyOtpPaste(
  otp: string[],
  index: number,
  pasteData: string,
): { otp: string[]; lastFilledIndex: number } | null {
  if (!/^\d+$/.test(pasteData)) return null;
  const updated = [...otp];
  let lastFilledIndex = index;
  for (let i = 0; i < pasteData.length; i++) {
    if (index + i < otp.length) {
      updated[index + i] = pasteData[i];
      lastFilledIndex = index + i;
    }
  }
  return { otp: updated, lastFilledIndex };
}

/** Maps corporate local+domain to special override emails (preserve legacy). */
export function resolveCorporateReferrerEmail(
  localPart: string,
  domain: string,
): string {
  let email = domain ? `${localPart}${domain}` : localPart;
  if (email === "skejriwal@hcltech.com" || email === "skejriwal@hcl.com") {
    email = "skejriwal@univoedtech.com";
  } else if (email === "dkaura@hcltech.com" || email === "dkaura@hcl.com") {
    email = "dkaura@univoedtech.com";
  } else if (email.split("@")[0] === "jkumarunivoedtech") {
    email = "jkumarunivoedtech@gmail.com";
  } else if (email?.split("@")?.[0]?.toLowerCase() === "tech-qa") {
    email = "tech-qa@univoedtech.com"
  } else if (email?.split("@")?.[0]?.toLowerCase() === "techtools") {
    email = "techtools@univoedu.com"
  }
  return email;
}

export async function sendCheckStatusOtp(params: {
  emailOrPhone: string;
  otpFor: CheckStatusOtpFor;
}): Promise<{ ok: boolean; data?: any; response?: any; error?: unknown }> {
  const isEmail = params.emailOrPhone.includes("@");
  try {
    const response = await fetch("/api/referral/checkstatus/otp", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: isEmail
        ? JSON.stringify({
            Email: params.emailOrPhone,
            OTPFor: params.otpFor,
            isEmail: true,
          })
        : JSON.stringify({
            Phone: params.emailOrPhone,
            OTPFor: params.otpFor,
            isEmail: false,
          }),
    });
    const data = await response.json();
    const res = await data.response;
    return { ok: true, data, response: res };
  } catch (error) {
    console.error("Error fetching OTP:", error);
    return { ok: false, error };
  }
}

export async function sendCorporateReferralOtp(
  email: string,
): Promise<{ ok: boolean; success?: boolean; otp?: unknown; error?: unknown }> {
  try {
    const response = await fetch("/api/referral/sendOtp", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email }),
    });
    const res = await response.json();
    return { ok: true, success: res.success, otp: res.otp };
  } catch (error) {
    console.error("Error fetching OTP:", error);
    return { ok: false, error };
  }
}

export async function verifyReferralOtp(params: {
  mobile: string;
  otp: string;
  isCorporateReferralPage: boolean;
  hash: unknown;
}): Promise<{ success: boolean, response?: any }> {
  try{
    let url = "";
    if(params.isCorporateReferralPage){
      url = "/api/referral/checkstatus/verifyCorporateReferral";
    }else{
      url = "/api/referral/checkstatus/verify";
    }
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        mobile: params?.mobile,
        otp: params?.otp,
      }),
    });

    const data = await response.json();
    if (!data.success) {
      return { success: false, response: data };
    }

    return { success: true, response: data };
  }catch(err){
    return { success: false };
  }
}
